import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const dollars = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function Card({ label, value, sub, testid }) {
  return (
    <div className="border-r border-b border-stone/40 bg-bone p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-stone">{label}</div>
      <div className="font-serif text-3xl text-ink mt-3 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-[0.18em] text-brass mt-2">{sub}</div>}
    </div>
  );
}

export default function AdminOverview() {
  const [kpi, setKpi] = useState(null);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [user, setUser] = useState(null);
  useEffect(() => { (async () => {
    try { setUser((await api.get("/auth/me")).data); } catch {}
    try { setKpi((await api.get("/admin/overview")).data); } catch {}
    try { setLeads((await api.get("/admin/leads?page_size=5")).data.items || []); } catch {}
    try { setPayments((await api.get("/admin/payments?page_size=5")).data.items || []); } catch {}
  })(); }, []);
  if (!kpi) return <div className="text-ink-soft text-sm">Loading operations…</div>;
  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="space-y-9" data-testid="admin-overview">
      <header>
        <h1 className="font-serif font-medium text-4xl sm:text-5xl text-ink tracking-tight">Operations.</h1>
        <p className="text-ink-soft mt-2 text-sm">{firstName}, here&rsquo;s what&rsquo;s moving today.</p>
      </header>

      {/* KPI 4×3 grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-stone/40">
        <Card label="Leads total" value={kpi.leads_total} sub={`+${kpi.leads_new_last_7d} last 7d`} testid="kpi-leads" />
        <Card label="Clients total" value={kpi.clients_total} sub={`${kpi.clients_by_tier.audit_only} audit-only`} testid="kpi-clients" />
        <Card label="Revenue total" value={dollars(kpi.revenue_total_cents)} sub={`${dollars(kpi.revenue_last_30d_cents)} last 30d`} testid="kpi-revenue" />
        <Card label="Tool runs" value={kpi.tool_runs_total} sub="all-time" testid="kpi-tool-runs" />

        <Card label="Platform" value={kpi.clients_by_tier.platform} sub="clients" testid="kpi-tier-platform" />
        <Card label="Florida Transfer" value={kpi.clients_by_tier.florida_transfer} sub="clients" testid="kpi-tier-fl" />
        <Card label="Selective & Elite" value={kpi.clients_by_tier.selective_elite} sub="clients" testid="kpi-tier-elite" />
        <Card label="Transcript audits" value={kpi.transcript_audits_total} sub="all-time" testid="kpi-audits" />

        <Card label="Email log" value={kpi.email_log_total} sub={`${kpi.email_log_failed_last_24h} failed last 24h`} testid="kpi-email-log" />
        <Card label="Referrals credited" value={kpi.referrals_credited_total} sub="all-time" testid="kpi-refs" />
        <Card label="Credits outstanding" value={dollars(kpi.credits_outstanding_cents)} sub="across all users" testid="kpi-credits" />
        <Card label="Revenue 30d" value={dollars(kpi.revenue_last_30d_cents)} sub="trailing window" testid="kpi-revenue-30d" />
      </div>

      {/* Two-column widgets */}
      <div className="grid lg:grid-cols-2 gap-7">
        <section className="border border-stone/40 bg-bone p-5" data-testid="widget-recent-leads">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Recent leads</div>
            <Link to="/admin/leads" className="text-[10px] uppercase tracking-[0.22em] text-ivy hover:text-brass">View all →</Link>
          </div>
          {leads.length === 0 ? <p className="text-sm text-ink-soft mt-4">No leads yet.</p> : (
            <div className="mt-4 border-t border-l border-stone/30">
              {leads.map((l) => (
                <div key={l.lead_id} className="border-r border-b border-stone/30 grid grid-cols-12 gap-3 px-3 py-2.5 items-baseline">
                  <div className="col-span-5 text-sm text-ink truncate">{l.name || "—"}</div>
                  <div className="col-span-4 text-xs text-ink-soft truncate font-mono">{l.email}</div>
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em]">
                    <span className={l.status === "new" ? "text-brass" : l.status === "qualified" ? "text-ivy" : "text-stone"}>{l.status}</span>
                  </div>
                  <div className="col-span-1 text-[10px] text-stone font-mono tabular-nums text-right">{(l.created_at || "").slice(5, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="border border-stone/40 bg-bone p-5" data-testid="widget-recent-payments">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Recent payments</div>
            <Link to="/admin/payments" className="text-[10px] uppercase tracking-[0.22em] text-ivy hover:text-brass">View ledger →</Link>
          </div>
          {payments.length === 0 ? <p className="text-sm text-ink-soft mt-4">No payments yet.</p> : (
            <div className="mt-4 border-t border-l border-stone/30">
              {payments.map((p) => (
                <div key={p.session_id} className="border-r border-b border-stone/30 grid grid-cols-12 gap-3 px-3 py-2.5 items-baseline">
                  <div className="col-span-4 text-xs text-ink-soft truncate font-mono">{(p.metadata?.user_email || p.metadata?.buyer_email || "—").toLowerCase()}</div>
                  <div className="col-span-3 text-[11px] text-ink truncate">{p.tier_id}</div>
                  <div className="col-span-2 text-sm text-ink tabular-nums">${Number(p.amount || 0).toFixed(0)}</div>
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em]">
                    <span className={p.payment_status === "paid" ? "text-ivy" : "text-brass"}>{p.payment_status || "—"}</span>
                  </div>
                  <div className="col-span-1 text-[10px] text-stone font-mono tabular-nums text-right">{(p.created_at || "").slice(5, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
